import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api as kassaApi } from "@/pages/kassa/api";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, ShieldCheck, Store, BookText, Lock, ArrowLeft, ArrowRight } from "lucide-react";

const ACCOUNTS = [
  { key: "admin", mode: "kassa", username: "admin", title: "Beheerder", sub: "Kassa · adminpaneel, producten, rapporten", icon: ShieldCheck, accent: "bg-blue-600" },
  { key: "clerk", mode: "kassa", chooseShop: true, title: "Kassier", sub: "Kassa · verkoopscherm per winkel", icon: Store, accent: "bg-emerald-600" },
  { key: "boekhouding", mode: "accounting", username: "BE0788952072", title: "Boekhouding & HR", sub: "Boekhouding, HR, rapporten, aankopen, price tags", icon: BookText, accent: "bg-indigo-600" },
];

const SHOPS = [
  { id: "brustem", name: "Singh Express Shop Brustem", address: "Luikersteenweg 310, 3800 Sint-Truiden" },
  { id: "sint-truiden", name: "Singh Express Shop Sint-Truiden", address: "Hasseltsesteenweg 68, 3800 Sint-Truiden" },
  { id: "leesberg", name: "De Leesberg", address: "Steenberg 13, 3500 Hasselt" },
];

export default function PortalLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [shop, setShop] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const acc = ACCOUNTS.find((a) => a.key === selected);
  const shopDoc = SHOPS.find((s) => s.id === shop);
  const kassaUsername = acc?.chooseShop ? shop : acc?.username;

  const choose = (key) => { setSelected(key); setShop(null); setPassword(""); setError(""); };
  const back = () => { setSelected(null); setShop(null); setPassword(""); setError(""); };
  const backToShops = () => { setShop(null); setPassword(""); setError(""); };
  const pickShop = (id) => { setShop(id); setPassword(""); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (acc.mode === "kassa") {
        await kassaApi("/auth/login", "POST", { username: kassaUsername, password });
        navigate("/kassa");
      } else {
        await login(acc.username, password);
        navigate("/hub");
      }
    } catch (err) {
      let msg;
      if (acc.mode === "accounting") {
        msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      } else {
        msg = err.message === "Failed to fetch" ? "Kan de kassaserver niet bereiken. Probeer opnieuw." : err.message;
      }
      setError(msg || "Aanmelden mislukt");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="portal-login">
      {/* left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden"
           style={{ background: "linear-gradient(160deg,#0b1e4a 0%,#123a8f 55%,#1d4ed8 100%)" }}>
        <div className="pointer-events-none absolute inset-0 opacity-[0.08]"
             style={{ backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)", backgroundSize: "44px 44px" }} />
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-blue-400/20 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="h-11 w-11 rounded-md bg-white/15 flex items-center justify-center backdrop-blur">
            <Building2 className="h-6 w-6" />
          </div>
          <div>
            <div className="font-heading font-semibold text-lg">Singh Express Shop BV</div>
            <div className="text-sm text-white/70">BE 0788.952.072</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">
            Kassa, boekhouding<br />&amp; bedrijfsbeheer
          </h1>
          <p className="mt-5 text-white/75 max-w-md text-base leading-relaxed">
            Eén toegang voor je winkel: meld je aan als kassier of beheerder voor de kassa,
            of met je BTW-nummer voor boekhouding en HR.
          </p>
        </div>
        <div className="relative text-xs text-white/50">Beveiligde toegang · Rolgebaseerde rechten · Onveranderlijke boekingen</div>
      </div>

      {/* right selection / password */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
              <Building2 className="h-5 w-5" />
            </div>
            <span className="font-heading font-semibold">Singh Express Shop</span>
          </div>

          {!selected ? (
            <>
              <h2 className="font-heading text-2xl font-medium tracking-tight">Wie ben je?</h2>
              <p className="text-sm text-muted-foreground mt-1 mb-6">Kies waarmee je wil aanmelden.</p>
              <div className="space-y-3">
                {ACCOUNTS.map((a) => (
                  <button key={a.key} type="button" data-testid={`portal-account-${a.key}`} onClick={() => choose(a.key)}
                          className="group w-full text-left rounded-xl border border-border bg-card p-4 flex items-center gap-4 shadow-sm hover:shadow-md hover:border-primary transition-all active:scale-[0.99]">
                    <div className={`h-12 w-12 shrink-0 rounded-lg flex items-center justify-center ${a.accent}`}>
                      <a.icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{a.title}</div>
                      <div className="text-xs text-muted-foreground truncate">{a.sub}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                  </button>
                ))}
              </div>
            </>
          ) : (acc.chooseShop && !shop) ? (
            <div data-testid="portal-shop-choice">
              <button type="button" data-testid="portal-back" onClick={back}
                      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
                <ArrowLeft className="h-4 w-4" /> Andere keuze
              </button>
              <h2 className="font-heading text-2xl font-medium tracking-tight">Welke winkel?</h2>
              <p className="text-sm text-muted-foreground mt-1 mb-6">Kies de winkel waar je aan de kassa staat.</p>
              <div className="space-y-3">
                {SHOPS.map((s) => (
                  <button key={s.id} type="button" data-testid={`portal-shop-${s.id}`} onClick={() => pickShop(s.id)}
                          className="group w-full text-left rounded-xl border border-border bg-card p-4 flex items-center gap-4 shadow-sm hover:shadow-md hover:border-primary transition-all active:scale-[0.99]">
                    <div className="h-12 w-12 shrink-0 rounded-lg flex items-center justify-center bg-emerald-600">
                      <Store className="h-6 w-6 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{s.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{s.address}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="portal-form">
              <button type="button" data-testid="portal-back" onClick={acc.chooseShop ? backToShops : back}
                      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
                <ArrowLeft className="h-4 w-4" /> {acc.chooseShop ? "Andere winkel" : "Andere keuze"}
              </button>
              <div className="flex items-center gap-3 mb-6">
                <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${acc.accent}`}>
                  <acc.icon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <div className="font-heading text-xl font-medium">{acc.chooseShop ? shopDoc.name : acc.title}</div>
                  <div className="text-xs text-muted-foreground">{acc.chooseShop ? shopDoc.address : <>Aanmelden als <span className="font-medium">{acc.username}</span></>}</div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="portal-password">Wachtwoord</Label>
                  <Input id="portal-password" type="password" data-testid="portal-password" value={password} autoFocus
                         onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
                </div>
                {error && (
                  <div data-testid="portal-error" className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">{error}</div>
                )}
                <Button type="submit" data-testid="portal-submit" disabled={loading}
                        className="w-full active:scale-[0.98] transition-transform">
                  <Lock className="h-4 w-4 mr-2" />
                  {loading ? "Bezig…" : "Aanmelden"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
